import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

export enum BookingStep {
  SEARCH = 'search',
  LIST = 'list',
  PAYMENT = 'payment',
}

export type PaymentStep = 1 | 2 | 3

interface SelectedRoom {
  roomId: string
  roomName: string
  quantity: number
  adults: number
  amount: number
}

interface CustomerInfo {
  fullName: string
  email: string
  phone: string
  note?: string
}

interface BookingState {
  currentStep: BookingStep
  currentPaymentStep: PaymentStep
  selectedHotel: string
  checkInDate: string
  checkOutDate: string
  adults: number
  kidsOver6: number
  kidsUnder6: number
  roomType: string
  selectedRooms: SelectedRoom[]
  customerInfo: CustomerInfo | null

  // Actions
  setCurrentStep: (step: BookingStep) => void
  setCurrentPaymentStep: (step: PaymentStep) => void
  setSearchData: (data: {
    hotelId: string
    checkInDate: string
    checkOutDate: string
    adults: number
    kidsOver6: number
    kidsUnder6: number
    roomType: string
  }) => void
  setCustomerInfo: (info: CustomerInfo) => void
  addRoom: (room: SelectedRoom) => void
  removeRoom: (roomId: string) => void
  clearBookingData: () => void
  getBookingData: () => {
    hotelId: string
    arrivalDate: string
    departureDate: string
    bookingItems: Array<{
      roomId: string
      quantity: number
      adults: number
      children: number
      extraAdults: number
      extraChildren: number
      discount: number
      amount: number
      deposit: number
    }>
    customerInfo: CustomerInfo | null
  }
}

export const useBookingFlow = create<BookingState>()(
  persist(
    (set, get) => ({
      // Initial state
      currentStep: BookingStep.SEARCH,
      currentPaymentStep: 1,
      selectedHotel: '',
      checkInDate: '',
      checkOutDate: '',
      adults: 1,
      kidsOver6: 0,
      kidsUnder6: 0,
      roomType: '',
      selectedRooms: [],
      customerInfo: null,

      // Actions
      setCurrentStep: (step) => {
        set({ currentStep: step })
      },

      setCurrentPaymentStep: (step) => {
        set({ currentPaymentStep: step })
      },

      setSearchData: (data) => {
        set({
          selectedHotel: data.hotelId,
          checkInDate: data.checkInDate,
          checkOutDate: data.checkOutDate,
          adults: data.adults,
          kidsOver6: data.kidsOver6,
          kidsUnder6: data.kidsUnder6,
          roomType: data.roomType,
          currentStep: BookingStep.LIST,
          currentPaymentStep: 1, // Reset payment step when setting new search
        })
      },

      setCustomerInfo: (info) => {
        set({ customerInfo: info })
      },

      addRoom: (room) => {
        set((state) => ({
          selectedRooms: [...state.selectedRooms, room],
        }))
      },

      removeRoom: (roomId) => {
        set((state) => ({
          selectedRooms: state.selectedRooms.filter((room) => room.roomId !== roomId),
        }))
      },

      clearBookingData: () => {
        set({
          currentStep: BookingStep.SEARCH,
          currentPaymentStep: 1,
          selectedHotel: '',
          checkInDate: '',
          checkOutDate: '',
          adults: 1,
          kidsOver6: 0,
          kidsUnder6: 0,
          roomType: '',
          selectedRooms: [],
          customerInfo: null,
        })
      },

      getBookingData: () => {
        const state = get()
        const bookingData = {
          hotelId: state.selectedHotel,
          arrivalDate: new Date(state.checkInDate).toISOString(),
          departureDate: new Date(state.checkOutDate).toISOString(),
          bookingItems: state.selectedRooms.map((room) => ({
            roomId: room.roomId,
            quantity: room.quantity,
            adults: room.adults,
            children: 0,
            extraAdults: 0,
            extraChildren: 0,
            discount: 0,
            amount: room.amount,
            deposit: 0,
          })),
          customerInfo: state.customerInfo,
        }
        return bookingData
      },
    }),
    {
      name: 'booking-flow',
      version: 1,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        // Persist all relevant state
        currentStep: state.currentStep,
        currentPaymentStep: state.currentPaymentStep,
        selectedHotel: state.selectedHotel,
        checkInDate: state.checkInDate,
        checkOutDate: state.checkOutDate,
        adults: state.adults,
        kidsOver6: state.kidsOver6,
        kidsUnder6: state.kidsUnder6,
        roomType: state.roomType,
        selectedRooms: state.selectedRooms,
        customerInfo: state.customerInfo,
      }),
    }
  )
)
